/**
 * CreateEventPublish — Step: Review & publish.
 * Shows a summary of everything entered, Save draft + Publish actions.
 * Brand review + approvals gate ports in a follow-on drop.
 */

import { useState } from "react";
import { Btn, Card, Icon, Modal, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { creation, events } from "@v0y4ge/api-client";
import { CreationShell } from "./CreationShell";
import { CreationStore, useCreationConfig, useCreationState, wizardNeighbors, REPEAT_LABELS_ALL, isPublicAnyone, requiredEventChecks, normalizeVirtualProvider, deriveEventIsLive } from "./store";
import { CREATION_CONFIGS } from "./types";
import { WizardCanvas } from "./wizard-utils";
import { useNav } from "../../../shell/router";
import { can } from "../../../shell/access";
import { upsertEvent, refetchEvents, useEvents } from "../useEvents";
import { uiEventFromCreation, toUIEvent } from "../../../lib/events-adapter";
import { parseServerError, ServerErrors, stepForPath, stepLabel, type ServerErrorEnvelope } from "./server-errors";
import { upsertTemplate, newTemplateId } from "../../templates/useTemplates";
import { useQuestions } from "../../questions/useQuestions";
import { useSurveys } from "../../surveys/SurveyEditorPage";
import { useFlows } from "../../messaging/useFlows";
import { friendlyDateTime } from "../../../lib/datetime";
import type { EventTicket, EventTicketTypeConfigEntry, StudioEventDto } from "@v0y4ge/api-contracts";
import { useT } from "../../../lib/i18n";

// #CD-7 — the Schedule step's TimeZonePicker (jrni-ui) submits its display
// label "(GMT-5) America/New_York" as the value; the backend requires a pure
// IANA id and 422s INVALID_TIMEZONE on the raw label. Strip a leading
// "(GMT±N[:MM]) " prefix before it leaves the wizard so both newly-picked AND
// already-stored draft timezones publish. No-op when already IANA.
const stripTzPrefix = (v?: string): string | undefined => v ? v.replace(/^\(GMT[+-]?\d{1,2}(:\d{2})?\)\s*/, "") : v;

// #smoke-A (2026-07-09): the wire value must be the CANONICAL enum, never the
// dropdown's display label — "Public · Anyone with link" round-trips into D1
// verbatim and breaks the events-adapter's draft derivation on read
// (status:'scheduled' + visibility!=='public' ⇒ draft). Reuses the existing
// isPublicAnyone() label test; also passes through an already-canonical value
// (drafts saved after this fix store "public"/"private").
const canonicalVisibility = (v?: string | null): "public" | "private" | undefined =>
  !v ? undefined : (v === "public" || isPublicAnyone(v)) ? "public" : "private";

type Row = { k: string; v?: string; list?: string[] };
type Section = { t: string; route: string; icon: (p?: { size?: number }) => React.ReactNode; rows: Row[] };

function SummaryRow({ k, v, list }: Row) {
  if (!v && !(list && list.length)) return null;
  return (
    <div style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${T.border}`, alignItems: "flex-start" }}>
      <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.03, minWidth: 104, flexShrink: 0, paddingTop: 1 }}>{k}</span>
      {list && list.length ? (
        <span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
          {list.map((item, i) => <span key={i} style={{ fontSize: 13, color: T.n1, wordBreak: "break-word" }}>{item}</span>)}
        </span>
      ) : (
        <span style={{ fontSize: 13, color: T.n1, wordBreak: "break-word", flex: 1 }}>{v}</span>
      )}
    </div>
  );
}

function methodLabel(m?: { inPerson?: boolean; virtual?: boolean } | null) {
  if (!m) return "";
  if (m.inPerson && m.virtual) return "Hybrid";
  if (m.inPerson) return "In-person";
  if (m.virtual) return "Virtual";
  return "";
}

const VIRTUAL_PROVIDER_LABEL: Record<string, string> = { // ratified EVENT_VIRTUAL_PROVIDER_IDS (kit e670c958)
  zoom: "Zoom", webex: "Cisco Webex", goto: "GoTo Meeting", gmeet: "Google Meet", msteams: "Microsoft Teams", custom: "Custom link",
};

export function CreateEventPublish() {
  const t = useT();
  const nav = useNav();
  const cfg = useCreationConfig();
  const state = useCreationState();
  const isEditing = !!(state._editingId);
  const isTemplate = !!(state._templateMode);
  const { back } = wizardNeighbors(cfg, "create.publish", isTemplate);

  const persistTemplate = (status: "published" | "draft") => {
    upsertTemplate({
      id: (state._templateId as string) || newTemplateId(),
      typeId: state.typeId || "blank",
      name: String(cfg.title || "Untitled template"),
      type: String(cfg.label || "Template"),
      usage: 0,
      lastEdit: "Just now",
      hue: (cfg.heroTo as string) || (cfg.heroFrom as string) || T.p1,
      desc: String(cfg.description || ""),
      tags: Array.isArray(cfg.tags) ? (cfg.tags as string[]) : [],
      defaults: { capacity: Number(cfg.capacity || 0), tickets: ((cfg.tickets as EventTicket[]) || []).length, comms: 0 },
      status,
    });
  };
  const persistEvent = (status: "upcoming" | "draft", idOverride?: string) => {
    upsertEvent(uiEventFromCreation(cfg as Record<string, unknown>, { id: idOverride || (state._editingId as string) || (state._draftId as string) || undefined, status }));
  };

  // #smoke item-1 (2026-07-09): every post-publish affordance must use the
  // SERVER-returned id/slug — never a client-local draft id (ev_xxxx no API
  // knows). Capture both from the mutation response; the id also threads into
  // the cache upsert so list rows/eyeball/Live-tab links carry the real id.
  const captureServerIdentity = (res: Record<string, any> | null | undefined): string | undefined => {
    const id = res?.id ?? res?.event?.id ?? res?.data?.id;
    if (id) CreationStore.setDraftId(String(id));
    const sSlug = res?.slug ?? res?.event?.slug ?? res?.landing?.slug ?? res?.data?.slug;
    if (sSlug) CreationStore.setField("slug", String(sSlug));
    return id ? String(id) : undefined;
  };

  // F-87 (kit e670c958): modality + virtual provider persist via the mode ops
  // (GET/PUT /v1/admin/events/{id}/mode) — NOT via the createEvent location
  // fields. GET first (never 404s — the server synthesizes an in_person default)
  // so platform_mode / attendee_app_enabled, owned by the Event Day surfaces,
  // are preserved; check_in_only_flag is server-derived and never sent. Awaited
  // inside the same save/publish action: a mode failure surfaces as that
  // action's error (the event write already succeeded, so retry re-runs as
  // update + mode PUT).
  const persistEventMode = async (serverId: string | undefined) => {
    if (!serverId) return;
    const m = (cfg.method ?? {}) as { inPerson?: boolean; virtual?: boolean };
    const modality = m.inPerson && m.virtual ? ("hybrid" as const) : m.virtual ? ("virtual" as const) : ("in_person" as const);
    const provider: string | null = m.virtual ? (normalizeVirtualProvider((cfg as { virtualProvider?: string }).virtualProvider) || "custom") : null;
    const link = String((cfg as { virtualLink?: string }).virtualLink || "").trim();
    const current = await events.getEventMode(serverId);
    await events.setEventMode(serverId, {
      platform_mode: current.platform_mode,
      modality,
      // Interim cast per the allowRetroactive precedent: the checked-in dist
      // d.ts still carries the pre-e670c958 provider enum until the ingest
      // build regenerates it from the overlaid src (which ratifies these ids).
      virtual_provider: provider as Parameters<typeof events.setEventMode>[1]["virtual_provider"],
      // join_url key declared for ratification (2026-07-20 changelog) — only a
      // hand-pasted custom link rides config; provider-managed links are
      // server-owned (studio_events.meeting_link) and never echoed back.
      virtual_config: provider === "custom" && link ? { join_url: link } : undefined,
      attendee_app_enabled: current.attendee_app_enabled,
    });
  };

  // #713 re-graft (2026-07-08 merge): Greg's shared-store upsert above is the
  // CACHE update; the awaited client op below is the PERSISTENCE. Success
  // feedback + navigation fire only after the op resolves; failure renders the
  // error and stays (no-fake-interactivity mandate).
  //
  // 2026-07-12 docket §1/§3/§5: a 4xx write returns the canonical envelope
  // {code,message,fields[],requestId} — byte-identical from mock and backend.
  // Parse it (parseServerError), render fields[] inline at their owning steps
  // (ServerErrors + <FieldError>) plus the per-field card below with Fix jumps,
  // and NEVER advance or close the wizard on failure. EVENT_EDIT_MOVES_LIVE_TO_PAST
  // gets a confirm dialog; only an explicit confirm resends with allowRetroactive.
  const [busy, setBusy] = useState<"draft" | "publish" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [serverErr, setServerErr] = useState<ServerErrorEnvelope | null>(null);
  const [retro, setRetro] = useState<{ verb: "draft" | "publish"; env: ServerErrorEnvelope } | null>(null);
  // 2026-07-31: editing a live event no longer locks the wizard, but Publish is
  // still the action that actually writes changes onto the running event, so it
  // gets a lightweight confirm — the user affirms they know they're changing an
  // active event before the write goes out. Distinct from the `retro` modal
  // above (which fires only for the specific retroactive-date consequence).
  const { events: storedEvents } = useEvents();
  const storedEvent = state._editingId ? storedEvents.find((e) => e.id === state._editingId) : null;
  const eventIsLive = deriveEventIsLive(cfg, storedEvent);
  const [confirmLiveEdit, setConfirmLiveEdit] = useState(false);

  // Shared landing shapes (gate-verified): the contract's CreationSnapshot MERGES
  // LandingDesign, so `slug` + `landingDesign` are REQUIRED snapshot members even
  // when no page was designed. Fallbacks are the LandingBuilder's OWN defaults
  // (title-derived slug — same derivation as its slug initializer — empty blocks,
  // Brand theme, DM Sans), never invented content.
  type LandingInput = NonNullable<Parameters<typeof creation.createEvent>[0]["landing"]>;
  const designCfg = () => (cfg as { landingDesign?: { blocks?: unknown[]; theme?: string; font?: string } }).landingDesign;
  const landingDesignBody = (): LandingInput["landingDesign"] => {
    const d = designCfg();
    return {
      blocks: (Array.isArray(d?.blocks) ? d!.blocks! : []) as LandingInput["landingDesign"]["blocks"],
      theme: d?.theme || "Brand",
      font: d?.font || "DM Sans",
    };
  };
  const derivedSlug = (): string =>
    ((cfg as { slug?: string }).slug as string)
    || String(cfg.title || "Untitled event").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
    || "untitled-event";
  // #5 (2026-07-14 docket) — "Build from scratch" is a STARTING POINT, not a
  // category. A blank optional Event type must be OMITTED from the payload,
  // never defaulted to the template pick's display name (it leaked onto the
  // public attend page's eyebrow badge as "BUILD FROM SCRATCH"). Real type
  // labels (template picks / typed starting points) still flow through.
  const categoryLabel = (): string | undefined => {
    const l = String(cfg.label || "").trim();
    if (!l) return undefined;
    if (state.typeId === "blank" && l === (CREATION_CONFIGS.blank?.label || "Build from scratch")) return undefined;
    return l;
  };

  // #816 (2026-07-14 docket, DEMO-BLOCKING) — the Registration step's "Standard
  // attendee fields" (cfg.stdFields) map onto the contract's registrationQuestions
  // and MUST ride every create/update/save-draft, else per-field required/optional
  // is silently dropped (every Longhorn event carried empty
  // registration_questions_json, so #815's SSR required-render had nothing to show).
  // Shape is EXACTLY StudioRegistrationQuestionItemSchema (api-contracts
  // events.ts:817) — the backend read schema is .strict() over the array, so ONE
  // unknown key on ANY item collapses the WHOLE array to [] on read. Rules:
  // firstName/lastName/email are locked always-required; 'off' on a non-default
  // field (homeAddress/company/title) OMITS that question; phone 'off' has no
  // contract representation today → sent as optional (hidden standard fields are
  // an explicitly-backlogged contract add — not built here). Labels match the
  // Registration step's own STD_FIELDS so the public form reads what the admin saw.
  const registrationQuestions = () => {
    const modes = ((cfg as { stdFields?: Record<string, "required" | "optional" | "off"> }).stdFields) || {};
    // Defaults mirror the Registration step's initial state (WYSIWYG when the
    // step was never opened): phone required, the other optional fields optional.
    const mode = (k: string, dflt: "required" | "optional") => modes[k] || dflt;
    const qs: Array<{ id: string; fieldKey?: "firstName" | "lastName" | "email" | "phone"; label: string; type: "short" | "long" | "email" | "phone"; validation?: "email" | "phone-e164"; required: boolean }> = [
      { id: "default-firstName", fieldKey: "firstName", label: t("events.creationCreateEventPublish.firstName", "First name"), type: "short", required: true },
      { id: "default-lastName", fieldKey: "lastName", label: t("events.creationCreateEventPublish.lastName", "Last name"), type: "short", required: true },
      { id: "default-email", fieldKey: "email", label: t("events.creationCreateEventPublish.emailAddress", "Email address"), type: "email", validation: "email", required: true },
      { id: "default-phone", fieldKey: "phone", label: t("events.creationCreateEventPublish.phoneNumber", "Phone number"), type: "phone", validation: "phone-e164", required: mode("phone", "required") === "required" },
    ];
    ([["homeAddress", "Home address", "long"], ["company", "Company / organisation", "short"], ["title", "Job title", "short"]] as const).forEach(([id, lbl, type]) => {
      const m = mode(id, "optional");
      if (m === "off") return;
      qs.push({ id: `std-${id}`, label: lbl, type, required: m === "required" });
    });
    return qs;
  };

  const buildPayload = (status: "draft" | "live") => ({
    title: (cfg.title as string) || "Untitled event",
    // 2026-07-12 docket §4 (gate-verified): literal status:'draft' is NOT in the
    // update/saveDraft input enums and 422s schema-parse server-side until the
    // draft enum lands — a draft-intent write therefore OMITS `status` (their
    // stated interim: no publish intent ⇒ draft-flavored record; the adapter
    // derives Draft on read). JSON.stringify drops the undefined key.
    status: status === "live" ? ("live" as const) : undefined,
    type: state.typeId,
    label: categoryLabel(),
    visibility: canonicalVisibility(cfg.visibility as string | null),
    // fleet#760: the server-side publish gate (REQUIRED_FIELDS_MISSING, scoped
    // to creationSnapshot requests) requires the description the wizard already
    // collects — send it.
    description: String(cfg.description || "") || undefined,
    method: cfg.method ?? undefined,
    // cfg.start/end carry ISO 8601 + offset from the DatePicker contract (0707C).
    startsAt: (cfg.start as string) || undefined,
    endsAt: (cfg.end as string) || undefined,
    // Wire format is IANA-only (backend rejects the "(GMT-N) " display prefix).
    timezone: stripTzPrefix((cfg as { timezone?: string }).timezone) || undefined,
    // #749 root-cause-3: the venue address was captured in UI state but never
    // sent. locationAddress must be the STRING (backend's EventMutationSchema
    // already declares it); the structured geo object stays client-side only.
    locationAddress: (() => { const a = (cfg as { address?: unknown }).address; return typeof a === "string" && a ? a : undefined; })(),
    // #749 residual-1 (CD-BOUNCE-drop19): the resolved structured location must ALSO
    // reach the server when the address was geo-validated — the string alone can't
    // drive the published-events-are-geocoded invariant or the WYSIWYG map block.
    // Shape = EventLocation (api-contracts events.ts): address object + optional
    // lat/lng/tz/placeId + required `validated`. Sent only when geo.validated is set;
    // an entered-but-unresolved free-text address stays string-only (validated:false
    // path never fabricates coordinates).
    location: (() => {
      const g = (cfg as { geo?: { validated?: boolean; address?: Record<string, string> | null; lat?: number; lng?: number; tz?: string; placeId?: string } }).geo;
      if (!g || !g.validated) return undefined;
      return { address: (g.address && typeof g.address === "object") ? g.address : {}, lat: g.lat, lng: g.lng, tz: g.tz, placeId: g.placeId, validated: true };
    })(),
    capacity: (cfg as { capacity?: number }).capacity ?? undefined,
    // Typed against the contract's EventTicket[] (drop-35 docket §c): cfg.tickets
    // is the wizard's ticket rows, which ARE EventTicket-shaped (id/name/price/
    // capacity/kind/visibility) — assert to the real type, not an `unknown[]`
    // escape hatch, so tsc validates the shape at the createEvent/updateEvent
    // boundary.
    tickets: (cfg.tickets as EventTicket[] | undefined) ?? undefined,
    // Per-event ticket-type overrides (2026-07-10 ingest): qty/kind/price per
    // selected preset type — declared for the create/update event op; the
    // flattened tickets[] above remains the read model. Backend must enforce
    // admin price locks (priceLocked types ignore a per-event price).
    // drop-37 §6: `kind` is the contract's strict enum (EventTicketTypeConfigEntry
    // — 'free'|'paid'|'donation'|'invite', packages/api-contracts/src/events.ts),
    // never a free string; any other value 422s EVENT_VALIDATION_ERROR. The store
    // value is TicketKind-typed (ticket-types-store), structurally identical.
    ticketTypeConfigs: (cfg as { ticketTypeConfigs?: Record<string, EventTicketTypeConfigEntry> }).ticketTypeConfigs ?? undefined,
    ticketTypeIds: (cfg as { ticketTypeIds?: string[] }).ticketTypeIds ?? undefined,
    // #816 — per-field required/optional for the standard attendee fields (see
    // registrationQuestions() above). The real events worker's EventMutationSchema
    // persists this verbatim (registrationQuestionsJson, studio-events.ts:2773);
    // the CONTRACT's StudioEventCreateInput doesn't declare it yet, so the MOCK
    // strips it — declared in the changelog as the additive contract/mock ask.
    registrationQuestions: registrationQuestions(),
    // Publish-path RCA (2026-07-09) + L1 (Voyage #272, 2026-07-25): the Landing
    // step's state was originally never sent; then it was sent ONLY when a page
    // had explicitly-designed blocks, on the assumption that the server default
    // materializes a row otherwise. L1's live-D1 evidence refuted that:
    // materializeWizardLandingDesign is GATED on the mutation carrying `landing`
    // — no field, no landing_pages row, public page 404s. So the wizard now
    // ALWAYS sends `landing` (slug falls back to the title-derived slug and
    // landingDesign to the builder's own defaults — the same values already
    // required inside creationSnapshot; no invented content).
    landing: ((): Parameters<typeof creation.createEvent>[0]["landing"] => {
      const slug = (cfg as { slug?: string }).slug || derivedSlug();
      if (!slug) return undefined; // no title yet — nothing derivable to persist
      return {
        slug: String(slug),
        landingHeadline: ((cfg as { landingHeadline?: string }).landingHeadline || undefined),
        landingSubhead: ((cfg as { landingSubhead?: string }).landingSubhead || undefined),
        useLanding: (cfg as { useLanding?: boolean }).useLanding ?? true,
        landingDesign: landingDesignBody(),
      };
    })(),
    // creationSnapshot carries the FULL wizard draft (CreationSnapshot / CreationDraft
    // shape) — its presence is the signal that scopes the server-side publish gate
    // (fleet#760). The previous 4-field object was missing CreationDraft's required
    // members (label/title/description/tags/start/end/timezone/repeatIndex) and so
    // wasn't assignable — that malformed nested object, plus the `unknown[]` tickets
    // above, are what poisoned buildPayload's return type and errored EVERY
    // create/update/saveDraft call site (drop-35 docket §b/§d). Now populated from
    // the SAME real cfg/state values already sent as typed top-level fields — no
    // fabricated data; empty means genuinely-unset.
    creationSnapshot: {
      typeId: state.typeId,
      label: categoryLabel() || "",  // #5 — never the "Build from scratch" placeholder
      title: (cfg.title as string) || "Untitled event",
      description: String(cfg.description || ""),
      tags: Array.isArray(cfg.tags) ? (cfg.tags as string[]) : [],
      method: cfg.method ?? { inPerson: false, virtual: false },
      visibility: canonicalVisibility(cfg.visibility as string | null) || "public",
      start: (cfg.start as string) || "",
      end: (cfg.end as string) || "",
      timezone: stripTzPrefix((cfg as { timezone?: string }).timezone) || "",
      repeatIndex: (cfg.repeatIndex as number) ?? 0,
      capacity: (cfg as { capacity?: number }).capacity ?? null,
      tickets: (cfg.tickets as EventTicket[] | undefined) ?? [],
      // CreationSnapshot merges LandingDesign — slug + landingDesign are required
      // members (gate-verified); values from the designed page when present, else
      // the builder's own derivation/defaults (see landingDesignBody/derivedSlug).
      slug: derivedSlug(),
      landingDesign: landingDesignBody(),
      typeIdLabel: state.typeId,
      overrides: state.overrides,
    },
  });

  // Save-as-template maps the wizard state onto the REAL EventTemplateCreateInput
  // (drop-35 docket §a): that schema has NO `config` field — it takes a template
  // blueprint (name/type/description/tags/defaults + defaultTicketTiers), NOT a
  // whole event object. Ticket setup carries via defaultTicketTiers; capacity via
  // defaults + defaultCapacity. Full-fidelity config carry-over (comms/questions)
  // is the declared `config?` additive ask (2026-07-11T1630 changelog) — omitted
  // here until it lands rather than smuggled through a strict schema.
  const buildTemplateInput = (status: "draft" | "published") => {
    const tiers = (Array.isArray(cfg.tickets) ? (cfg.tickets as EventTicket[]) : []).map((t) => ({
      id: t.id, name: t.name, price: t.price, capacity: t.capacity,
      visibility: (t.visibility === "hidden" ? "hidden" : "public") as "public" | "hidden",
    }));
    const cap = (cfg as { capacity?: number }).capacity ?? undefined;
    return {
      name: (cfg.title as string) || "Untitled template",
      type: state.typeId,
      description: String(cfg.description || "") || undefined,
      tags: Array.isArray(cfg.tags) ? (cfg.tags as string[]) : undefined,
      defaults: { capacity: cap ?? 0, tickets: tiers.length, comms: 0 },
      defaultCapacity: cap,
      defaultTicketTiers: tiers.length ? tiers : undefined,
      defaultLandingHeadline: ((cfg as { landingHeadline?: string }).landingHeadline) || undefined,
      defaultLandingSubhead: ((cfg as { landingSubhead?: string }).landingSubhead) || undefined,
      status,
    };
  };
  const fail = (e: unknown, verb: string) => {
    console.warn(verb + " failed:", e);
    const env = parseServerError(e);
    const editId = state._editingId || state._draftId;
    if (env && env.code === "EVENT_EDIT_MOVES_LIVE_TO_PAST" && editId) {
      // Confirm + override (docket §3): surface the consequence, resend with
      // allowRetroactive ONLY on explicit confirm — never swallow, never auto-retry.
      setRetro({ verb: verb === "Publish" ? "publish" : "draft", env });
      setBusy(null);
      return;
    }
    if (env) {
      setServerErr(env);
      ServerErrors.set(env); // powers <FieldError> inline on the owning steps
    } else {
      setErr(`Couldn't ${verb.toLowerCase()}. Nothing was lost — your entries are still here. Retry in a moment.`);
    }
    setBusy(null);
  };

  // Explicit cancel of the retroactive-date confirm — the server error is NOT
  // swallowed: it renders as the endsAt field error + card so the user can fix
  // the dates instead.
  const dismissRetro = () => {
    if (!retro) return;
    setServerErr(retro.env);
    ServerErrors.set(retro.env);
    setRetro(null);
  };

  const confirmRetro = async () => {
    if (!retro || busy) return;
    const { verb } = retro;
    setRetro(null); setBusy(verb); setErr(null);
    try {
      const id = String(state._editingId || state._draftId);
      // allowRetroactive is accepted by backend + mock today (docket §3, verified
      // their side); it lands in the published CreationEventUpdateInput type with
      // the draft-enum drop — the cast is the declared interim, not a workaround.
      const payload = { ...buildPayload(verb === "publish" ? "live" : "draft"), allowRetroactive: true } as unknown as Parameters<typeof creation.updateEvent>[1];
      const res = verb === "publish"
        ? await creation.updateEvent(id, payload)
        : await creation.saveEventDraft(id, payload as unknown as Parameters<typeof creation.saveEventDraft>[1]);
      captureServerIdentity(res as Record<string, any>);
      await persistEventMode(id); // F-87 — retro-confirmed writes still carry the mode row
      // The override moves the event into the past — reflect the SERVER's resulting
      // status (completed → Ended) in the cache, not an assumed upcoming/draft.
      upsertEvent(toUIEvent(res as StudioEventDto));
      void refetchEvents(); // docket §1c — server rows win after the awaited update
      if (verb === "draft") CreationStore.setField("_savedDraft", true);
      setBusy(null);
      nav.go("events");
    } catch (e) { fail(e, verb === "publish" ? "Publish" : "Save draft"); }
  };

  const handleSaveDraft = async () => {
    if (busy) return;
    setBusy("draft"); setErr(null); setServerErr(null); ServerErrors.set(null);
    try {
      if (isTemplate) {
        // Edit-template sessions carry _templateId (loadTemplateForEdit) — saving
        // UPDATES that template; only a fresh template session creates one.
        const tmplId = state._templateId as string | undefined;
        if (tmplId) {
          await creation.updateEventTemplate(tmplId, buildTemplateInput("draft"));
        } else {
          await creation.createEventTemplate(buildTemplateInput("draft"));
        }
        persistTemplate("draft");
      } else {
        const existingId = state._editingId || state._draftId;
        let serverId: string | undefined = existingId ? String(existingId) : undefined;
        if (existingId) {
          const res = await creation.saveEventDraft(existingId as string, buildPayload("draft")) as Record<string, any>;
          serverId = captureServerIdentity(res) ?? serverId;
        } else {
          const res = await creation.createEvent(buildPayload("draft")) as Record<string, any>;
          serverId = captureServerIdentity(res) ?? serverId;
        }
        await persistEventMode(serverId); // F-87 — mode row rides every save
        persistEvent("draft", serverId);
        void refetchEvents(); // docket §1c — refetch after the awaited create/saveDraft
      }
      CreationStore.setField("_savedDraft", true);
      setBusy(null);
      nav.go(isTemplate ? "templates" : "events");
    } catch (e) { fail(e, "Save draft"); }
  };

  const handlePublish = async () => {
    if (busy) return;
    if (!readyToPublish) { nav.go(missingReq[0].route); return; }
    if (isEditing && eventIsLive && !confirmLiveEdit) { setConfirmLiveEdit(true); return; }
    setBusy("publish"); setErr(null); setServerErr(null); ServerErrors.set(null);
    try {
      if (isTemplate) {
        const tmplId = state._templateId as string | undefined;
        if (tmplId) {
          await creation.updateEventTemplate(tmplId, buildTemplateInput("published"));
        } else {
          await creation.createEventTemplate(buildTemplateInput("published"));
        }
        persistTemplate("published");
        CreationStore.setField("_published", true);
        setBusy(null);
        nav.go("templates");
        return;
      }
      let serverId: string | undefined;
      if (state._editingId || state._draftId) {
        serverId = String(state._editingId || state._draftId);
        const res = await creation.updateEvent(serverId, buildPayload("live")) as Record<string, any>;
        serverId = captureServerIdentity(res) ?? serverId;
      } else {
        const res = await creation.createEvent(buildPayload("live")) as Record<string, any>;
        serverId = captureServerIdentity(res);
      }
      await persistEventMode(serverId); // F-87 — publish triggers the server-side provider meeting sync
      persistEvent("upcoming", serverId);
      void refetchEvents(); // docket §1c — refetch after the awaited create/update (publish)
      CreationStore.setField("_published", true);
      setBusy(null);
      nav.go("create.live");
    } catch (e) { fail(e, "Publish"); }
  };

  const canPublish = can("events.write");

  // Required-field completeness — single source of truth (store.requiredEventChecks).
  const reqChecks = requiredEventChecks(cfg as Record<string, unknown>, { template: isTemplate });
  const missingReq = reqChecks.filter((c) => !c.done);
  // Addendum item 13 (#256): an event with zero sellable capacity is dead on
  // arrival for landing/registration/checkout — Publish is blocked with an
  // inline reason until total capacity across ticket types is > 0.
  const totalTicketCapacity = ((cfg.tickets as { capacity?: number | string }[] | undefined) ?? []).reduce((s, tk) => s + (Number(tk.capacity) || 0), 0);
  const zeroCapacity = !isTemplate && totalTicketCapacity <= 0;
  const readyToPublish = missingReq.length === 0 && !zeroCapacity;

  // #4 (2026-07-14 docket, DEMO-BLOCKING) — the Schedule review card rendered
  // startsAt/endsAt WITHOUT the event's own timezone, printing the raw UTC
  // instant ("4:00 AM" for a midnight-EDT event) while the Event summary rail on
  // the SAME screen showed 12:00 AM. Derive the IANA zone exactly like the
  // summary dock does (strip the "(GMT-N) " display prefix) and pass it to every
  // friendlyDateTime on this screen so both surfaces agree pre-publish.
  const evTz = stripTzPrefix((cfg as { timezone?: string }).timezone) || undefined;

  // ── Per-step review breakdown ──────────────────────────────────────────────
  const qstore = useQuestions();
  const { surveys } = useSurveys();
  const flowsStore = useFlows();

  const tickets = (cfg.tickets || []) as Array<{ name?: string; price?: number; kind?: string; capacity?: number }>;
  const money = (t: { kind?: string; price?: number }) => (t.kind === "free" || t.kind === "code" || !t.price) ? "Free" : `$${Number(t.price).toLocaleString()}`;

  const tags = (Array.isArray(cfg.tags) ? cfg.tags as string[] : []).filter(Boolean);

  // Location summary line(s)
  const m = cfg.method || { inPerson: false, virtual: false };
  const locLines: string[] = [];
  if (m.inPerson) {
    const venue = (cfg as { venueName?: string }).venueName || "";
    const addr = (() => { const a = (cfg as { address?: unknown }).address; return typeof a === "string" ? a : ""; })(); // belt-and-suspenders: address must be a string (see #749 root cause)
    if (venue) locLines.push(venue);
    if (addr) locLines.push(addr);
    if (!venue && !addr) locLines.push("In-person · venue not set");
  }
  if (m.virtual) {
    const prov = normalizeVirtualProvider((cfg as { virtualProvider?: string }).virtualProvider);
    locLines.push(`Online · ${VIRTUAL_PROVIDER_LABEL[prov] || prov || "provider not set"}`);
  }

  // Booking questions + surveys (resolve names from libraries)
  const bq = ((cfg as { bookingQuestions?: { kind: string; id: string }[] }).bookingQuestions) || [];
  const bqLines = bq.map((it) => {
    if (it.kind === "group") { const g = qstore.groups().find((x) => x.id === it.id); return g ? `${g.name} (group · ${(g.items || []).length})` : "Group"; }
    const q = qstore.questions().find((x) => x.id === it.id); return q ? q.label : "Question";
  });
  const svIds = ((cfg as { surveys?: string[] }).surveys) || [];
  const svLines = svIds.map((id) => { const s = surveys.find((x) => x.id === id); return s ? s.name : id; });

  // Guests / +1s
  const allowGuests = !!(cfg as { allowGuests?: boolean }).allowGuests;
  const maxGuests = (cfg as { maxGuests?: number }).maxGuests ?? 1;
  const guestsVal = allowGuests ? `Allowed · up to ${maxGuests} per registration` : "Not allowed";

  // Consent
  const consent: string[] = [];
  if ((cfg as { collectMarketing?: boolean }).collectMarketing !== false) consent.push("Marketing opt-in");
  if ((cfg as { collectShareConsent?: boolean }).collectShareConsent) consent.push("Contact-sharing consent");

  // Messaging
  const msgMode = (cfg as { messagingMode?: string }).messagingMode === "custom" ? "custom" : "library";
  const msgFlowId = (cfg as { messagingFlowId?: string }).messagingFlowId || "";
  const msgFlow = (cfg as { messagingFlow?: { steps?: unknown[] } }).messagingFlow;
  const flowName = msgFlowId ? (flowsStore.all().find((f) => f.id === msgFlowId)?.name || "Selected flow") : "";
  const msgVal = msgMode === "custom"
    ? `Custom per-event flow · ${(msgFlow?.steps || []).length} step${(msgFlow?.steps || []).length === 1 ? "" : "s"}`
    : flowName ? `Library flow · ${flowName}` : "No flow selected";

  // Guest list
  const guestList = (cfg.guestList || []) as Array<{ kind?: string; count?: number }>;
  const guestCount = guestList.reduce((n, e) => n + (e.kind === "segment" ? (e.count || 0) : 1), 0);

  const repeatLabel = REPEAT_LABELS_ALL[(cfg as { repeatIndex?: number }).repeatIndex || 0] || "Doesn't repeat";
  const descRaw = (cfg.description as string) || "";
  const descShort = descRaw.length > 160 ? descRaw.slice(0, 157) + "…" : descRaw;

  // Landing — the hero headline/subhead are the event title & description, so the
  // summary reads those directly and stays consistent even if the landing step
  // wasn't revisited after an Essentials edit.
  const slug = (cfg as { slug?: string }).slug || "";
  const landingHeadline = (cfg.title as string) || (cfg as { landingHeadline?: string }).landingHeadline || "";
  const landingSubhead = descShort || (cfg as { landingSubhead?: string }).landingSubhead || "";

  const sections: Section[] = [
    {
      t: "Basics", route: "create.basics", icon: Icon.edit,
      rows: [
        { k: "Title", v: cfg.title as string },
        { k: "Type", v: categoryLabel() },  // #5 — hides the starting-point placeholder
        { k: "Description", v: descShort },
        { k: "Tags", list: tags.length ? tags : undefined },
      ],
    },
    {
      t: "Details", route: "create.details", icon: Icon.loc,
      rows: [
        { k: "Format", v: methodLabel(cfg.method) },
        { k: "Visibility", v: cfg.visibility as string },
        { k: "Host", v: (cfg as { host?: string }).host || "" },
        { k: "Location", list: locLines.length ? locLines : undefined },
      ],
    },
    ...(!isTemplate ? [{
      t: "Schedule", route: "create.dates", icon: Icon.calendar,
      rows: [
        { k: "Starts", v: cfg.start ? friendlyDateTime(cfg.start as string, evTz) : "" },
        { k: "Ends", v: cfg.end ? friendlyDateTime(cfg.end as string, evTz) : "" },
        { k: "Time zone", v: (cfg as { timezone?: string }).timezone || "" },
        { k: "Repeats", v: repeatLabel },
      ] as Row[],
    }] : []),
    {
      t: "Tickets", route: "create.tickets", icon: Icon.ticket,
      rows: [
        { k: "Tickets", list: tickets.length ? tickets.map((t) => `${t.name || "Ticket"} — ${money(t)} · ${t.capacity ?? "∞"} cap`) : undefined, v: tickets.length ? undefined : "No tickets configured" },
        { k: "Capacity", v: cfg.capacity ? `${Number(cfg.capacity).toLocaleString()} seats` : "" },
        { k: "Waitlist", v: (cfg as { waitlist?: number }).waitlist ? `${Number((cfg as { waitlist?: number }).waitlist).toLocaleString()} seats` : "" },
      ],
    },
    {
      t: "Registration", route: "create.registration", icon: Icon.list,
      rows: [
        { k: "Opens", v: (cfg as { regOpens?: string }).regOpens ? friendlyDateTime((cfg as { regOpens?: string }).regOpens as string, evTz) : "On publish" },
        { k: "Closes", v: (cfg as { regCloses?: string }).regCloses ? friendlyDateTime((cfg as { regCloses?: string }).regCloses as string, evTz) : "At event start" },
        { k: "Additional guests", v: guestsVal },
        { k: "Booking questions", list: bqLines.length ? bqLines : undefined, v: bqLines.length ? undefined : "None" },
        { k: "Surveys", list: svLines.length ? svLines : undefined, v: svLines.length ? undefined : "None" },
        { k: "Consent", list: consent.length ? consent : undefined, v: consent.length ? undefined : "None" },
      ],
    },
    {
      t: "Landing page", route: "create.landing", icon: Icon.browser,
      rows: [
        { k: "URL", v: slug ? `/${slug}` : "" },
        { k: "Headline", v: landingHeadline },
        { k: "Subhead", v: landingSubhead },
      ],
    },
    {
      t: "Messaging", route: "create.messaging", icon: Icon.mail,
      rows: [{ k: "Flow", v: msgVal }],
    },
    ...(!isTemplate && guestCount ? [{
      t: "Guest list", route: "create.guests", icon: Icon.users,
      rows: [{ k: "Invited", v: `${guestCount.toLocaleString()} ${guestCount === 1 ? "person" : "people"}` }] as Row[],
    }] : []),
  ];

  return (
    <CreationShell step={isTemplate ? 6 : 7} footer={
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
        <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14} />} onClick={() => nav.go(back)}>{t("common.actions.back", "Back")}</Btn>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn variant="secondary" icon={<Icon.edit size={14} />} onClick={handleSaveDraft} disabled={busy !== null}>
            {busy === "draft" ? t("events.createEventPublish.saving", "Saving…") : isTemplate ? t("events.createEventPublish.saveTemplateDraft", "Save template draft") : t("events.createEventPublish.saveDraft", "Save draft")}
          </Btn>
          {canPublish ? (
            <Btn icon={<Icon.send size={14} />} onClick={handlePublish} disabled={busy !== null || !readyToPublish} title={readyToPublish ? undefined : zeroCapacity && missingReq.length === 0 ? t("events.createEventPublish.zeroCapacityBlock", "Total ticket capacity is 0 — add capacity on the Tickets step before publishing.") : t("events.createEventPublish.completeRequiredFieldCount.icu", { count: missingReq.length })}>
              {busy === "publish" ? t("events.createEventPublish.publishing", "Publishing…") : isTemplate ? t("events.createEventPublish.publishTemplate", "Publish template") : isEditing ? t("events.createEventPublish.publishChanges", "Publish changes") : t("events.createEventPublish.publishEvent", "Publish event")}
            </Btn>
          ) : (
            <Btn disabled icon={<Icon.send size={14} />} title={t("events.creationCreateEventPublish.yourRoleCanBuildButNotPublishAskACoordinatorOrAdmin", "Your role can build but not publish — ask a Coordinator or Admin.")}>
              {isEditing ? t("events.createEventPublish.publishChanges", "Publish changes") : t("events.createEventPublish.publishEvent", "Publish event")}
            </Btn>
          )}
        </div>
      </div>
    }>
      <WizardCanvas width="wide" cols="stack">
        {/* Server-returned validation (docket §1) — the canonical envelope, rendered
            per-field with a Fix jump to the owning step; whole-request codes (no
            fields[]) render as a single banner from error.message. Stay on this step. */}
        {serverErr ? (
          <Card role="alert" data-testid="publish-server-error-card" style={{ padding: 16, background: T.red2, borderColor: T.red1 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ color: T.red1, flexShrink: 0 }}><Icon.warn size={16} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>
                  {serverErr.fields.length
                    ? /* TODO(i18n): replace template-literal with t('events.creationCreateEventPublish.text', { ... }) */`The server couldn't save this event — fix ${serverErr.fields.length === 1 ? "1 field" : `${serverErr.fields.length} fields`} and retry`
                    : serverErr.message}
                </div>
                {serverErr.fields.length ? (
                  <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                    {serverErr.fields.map((f, i) => {
                      const step = stepForPath(f.path);
                      return (
                        <li key={`${f.path}-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 13, color: T.n1 }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: T.red1, flexShrink: 0 }} />
                            <span>{f.message}{step ? <span style={{ color: T.soft }}> · {stepLabel(step)}</span> : null}</span>
                          </span>
                          {step ? <Btn variant="ghost" size="sm" icon={<Icon.arrow dir="right" size={12} />} onClick={() => nav.go(step)}>{t("events.creationCreateEventPublish.fix", "Fix")}</Btn> : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {serverErr.requestId ? <div style={{ fontSize: 11, color: T.soft, marginTop: 8 }}>{t("events.creationCreateEventPublish.ref", "Ref:")}{" "}{serverErr.requestId}</div> : null}
              </div>
            </div>
          </Card>
        ) : null}
        {/* Transport / unknown failure — stay on this step, nothing lost (#713) */}
        {err ? (
          <Card style={{ padding: 16, background: T.red2, borderColor: T.red1 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ color: T.red1, flexShrink: 0 }}><Icon.warn size={16} /></span>
              <div style={{ fontSize: 13, color: T.n1 }}>{err}</div>
            </div>
          </Card>
        ) : null}

        {/* Live-event confirm (2026-07-31, operator) — the wizard no longer locks
            editing while an event is live; this is the one checkpoint before a
            change actually reaches the running event. */}
        <Modal open={confirmLiveEdit} onClose={() => setConfirmLiveEdit(false)} title={t("events.createEventPublish.applyChangesToTheLiveEvent", "Apply changes to the live event?")} maxWidth={460}>
          <Modal.Body>
            <p style={{ margin: 0 }}>{t("events.createEventPublish.thisEventIsActiveAndRegistrationIsOpenPublishingWill", "This event is active and registration is open. Publishing will update the live event immediately — guests may see the changes right away.")}</p>
          </Modal.Body>
          <Modal.Footer>
            <Btn variant="secondary" onClick={() => setConfirmLiveEdit(false)} testid="create-publish-live-confirm-cancel">{t("events.creationCreateEventPublish.keepEditing", "Keep editing")}</Btn>
            <Btn icon={<Icon.send size={14} />} onClick={() => { setConfirmLiveEdit(false); void handlePublish(); }} testid="create-publish-live-confirm-confirm">{t("events.createEventPublish.publishChanges", "Publish changes")}</Btn>
          </Modal.Footer>
        </Modal>

        {/* Retroactive-date confirm (docket §3) — editing a live event's dates
            entirely into the past ends it. Resend with allowRetroactive only on
            explicit confirm; cancel keeps the server error rendered. */}
        <Modal open={!!retro} onClose={dismissRetro} title={t("events.creationCreateEventPublish.moveThisLiveEventIntoThePast", "Move this live event into the past?")} maxWidth={460}>
          <Modal.Body>
            <p style={{ margin: 0 }}>{t("events.creationCreateEventPublish.theseDatesEndBeforeNowSavingThemEndsTheLiveEventImmediately", "These dates end before now. Saving them ends the live event immediately — it moves to Past, registration closes, and day-of check-in stops.")}</p>
            {retro ? <p style={{ margin: "10px 0 0", fontSize: 13, color: T.n2 }}>{retro.env.message}</p> : null}
          </Modal.Body>
          <Modal.Footer>
            <Btn variant="secondary" onClick={dismissRetro} testid="create-publish-retro-cancel">{t("events.creationCreateEventPublish.keepEditing", "Keep editing")}</Btn>
            <Btn danger onClick={confirmRetro} testid="create-publish-retro-confirm">{t("events.creationCreateEventPublish.endEventAndSave", "End event and save")}</Btn>
          </Modal.Footer>
        </Modal>
        {/* Header */}
        <Card style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24 }}>
            <div>
              <h2 style={{ ...tx.h2, fontWeight: 700 }}>{isEditing ? t('events.creationCreateEventPublish.text2') : isTemplate ? t('events.creationCreateEventPublish.text3') : t('events.creationCreateEventPublish.text4')}</h2>
              <p style={{ ...tx.help, marginTop: 4 }}>
                {isEditing
                  ? t('events.creationCreateEventPublish.text5')
                  : isTemplate
                    ? t('events.creationCreateEventPublish.text6')
                    : t('events.creationCreateEventPublish.text7')}
              </p>
              {!canPublish ? (
                <div style={{ marginTop: 10, padding: "8px 12px", background: T.orange2, border: `1px solid ${T.orange1}`, borderRadius: 6, fontSize: 12, color: T.n1, display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Icon.lock size={12} /> {t("events.creationCreateEventPublish.yourRoleCanBuildButNotPublishAskACoordinatorOrAdmin", "Your role can build but not publish — ask a Coordinator or Admin.")}</div>
              ) : null}
            </div>
            <span style={{ width: 56, height: 56, borderRadius: 8, flexShrink: 0, background: cfg.heroImage ? `url(${cfg.heroImage as string}) center/cover` : `linear-gradient(135deg, ${cfg.heroFrom || T.p2}, ${cfg.heroTo || T.p1})`, color: T.white, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
              {cfg.heroImage ? "" : (cfg.heroLetters as string || "EV")}
            </span>
          </div>
        </Card>

        {/* Required-field completeness */}
        <Card style={{ padding: 24, background: readyToPublish ? T.green2 : T.orange2, border: `1px solid ${readyToPublish ? T.green1 : T.orange1}` }}>
          <div style={{ display: "flex", gap: 12 }}>
            <span style={{ color: readyToPublish ? T.green1 : T.orange1, flexShrink: 0 }}>{readyToPublish ? <Icon.check size={16} /> : <Icon.warn size={16} />}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>
                {readyToPublish
                  ? t('events.creationCreateEventPublish.text8')
                  : /* TODO(i18n): replace template-literal with t('events.creationCreateEventPublish.text9', { ... }) */`${missingReq.length} required field${missingReq.length === 1 ? "" : "s"} still needed`}
              </div>
              {readyToPublish ? (
                <div style={{ fontSize: 13, color: T.n2, marginTop: 4 }}>{t("events.creationCreateEventPublish.thisEventHasEverythingItNeedsToPublish", "This event has everything it needs to publish.")}</div>
              ) : (
                <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                  {missingReq.map((c, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 13, color: T.n1 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 6, height: 6, borderRadius: 999, background: T.orange1, flexShrink: 0 }} />{c.label}</span>
                      <Btn variant="ghost" size="sm" icon={<Icon.arrow dir="right" size={12} />} onClick={() => nav.go(c.route)}>{t("events.creationCreateEventPublish.fix", "Fix")}</Btn>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>

        {/* Publish warnings from type config */}
        {(cfg.publishWarnings as string[] | undefined)?.length ? (
          <Card style={{ padding: 24, background: T.orange2 }}>
            <div style={{ display: "flex", gap: 12 }}>
              <span style={{ color: T.orange1 }}><Icon.warn size={16} /></span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.orange1 }}>
                  {(cfg.publishWarnings as string[]).length === 1 ? t('events.creationCreateEventPublish.text10') : /* TODO(i18n): replace template-literal with t('events.creationCreateEventPublish.text11', { ... }) */`${(cfg.publishWarnings as string[]).length} checks`} {t("events.creationCreateEventPublish.beforePublishing", "before publishing")}</div>
                <ul style={{ margin: "6px 0 0", padding: 0, listStyle: "none", fontSize: 13, color: T.n1, display: "flex", flexDirection: "column", gap: 4 }}>
                  {(cfg.publishWarnings as string[]).map((w, i) => <li key={i}>· {w}</li>)}
                </ul>
              </div>
            </div>
          </Card>
        ) : null}

        {/* Summary — per-step breakdown */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16, alignItems: "start" }}>
          {sections.map((sec, i) => (
            <Card key={i} style={{ padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{sec.icon({ size: 15 })}</span>
                  <h3 style={{ ...tx.h3, fontWeight: 700, fontSize: 15 }}>{sec.t}</h3>
                </div>
                <Btn variant="ghost" size="sm" icon={<Icon.edit size={12} />} onClick={() => nav.go(sec.route)}>{t("common.actions.edit", "Edit")}</Btn>
              </div>
              <div>
                {sec.rows.some((r) => r.v || (r.list && r.list.length))
                  ? sec.rows.map((r, j) => <SummaryRow key={j} k={r.k} v={r.v} list={r.list} />)
                  : <div style={{ fontSize: 13, color: T.soft, padding: "8px 0" }}>{t("events.creationCreateEventPublish.nothingSetYet", "Nothing set yet.")}</div>}
              </div>
            </Card>
          ))}
        </div>
      </WizardCanvas>
    </CreationShell>
  );
}

export default CreateEventPublish;
